import { createContext, useContext, useState } from 'react'
import useSWR from 'swr'
import useAxios from '../../hooks/useAxios'
import fetchSelectedOrg from '../../services/organizations/fetchSelectedOrg'
import { OrganizationTypes } from '../../interfaces/organization'
import { ReportTypes } from '../../interfaces/report'
import { useAuthContext } from '../auth/auth'
import { Props } from '../auth/auth.interfaces'

interface ReportsCtxTypes {
  reports: ReportTypes[]
  selectedOrg: OrganizationTypes | null
  adminCompanyCode: string | null
  reportersReport: ReportTypes | null
  isReportLoading: boolean
  setReportersReport: (value: ReportTypes | null) => void
}

const reportsCtx = createContext<ReportsCtxTypes>({
  reports: [],
  selectedOrg: null,
  adminCompanyCode: null,
  reportersReport: null,
  isReportLoading: false,
  setReportersReport: () => null,
})

export const useReportsContext = (): ReportsCtxTypes => useContext(reportsCtx)

const ReportsProvider = ({ children }: Props) => {
  const { authClient, token } = useAuthContext()
  const [reportersReport, setReportersReport] = useState<ReportTypes | null>(
    null,
  )
  const { data: reports, isLoading: isReportLoading } = useAxios({
    url: '/report/report/',
    swrKey: `reports`,
  })

  const organizationId = authClient?.organization

  // GET CURRENT ORGANIZATION
  // Kept under the 'org' key so the existing mutate('org') callers - branch
  // create/edit, company rename, policy, logo - keep revalidating this,
  // branches included.
  const { data: organization } = useSWR(
    token && organizationId ? 'org' : null,
    () => fetchSelectedOrg(organizationId as number, token as string),
  )

  const ctx = {
    reports,
    selectedOrg: organization ?? null,
    adminCompanyCode: organization?.code ?? null,
    reportersReport,
    isReportLoading,
    setReportersReport,
  }

  return <reportsCtx.Provider value={ctx}>{children}</reportsCtx.Provider>
}

export default ReportsProvider
